import { getDefaultDataConfig, getDefaultQueryConfig, getDefaultViewConfig } from './configDefaults'

// Keys that belong to each config type
const DATA_CONFIG_KEYS = [
    'selectedItems',
    'categoryAxis',
    'drillAttribute',
    'filter',
    'savedFilter',
    'worksheetID',
    'rangeName',
    'sheetName',
    'selectedWorksheetName',
    'worksheetColumns',
    'selectedTableColumns',
    'selectedTableKpis',
    'selectedTables',
    'usedDrillAttributes',
    'allowedKPIs',
    'allowedReportSeriesTables',
]

const QUERY_CONFIG_KEYS = [
    'period',
    'timeRange',
    'calendarType',
    'from',
    'to',
    'sortColumns',
    'displayRows',
    'tableTimeSeries',
    'disableTimeSeries',
    'activePrompts',
]

const VIEW_CONFIG_KEYS = [
    'chartTitle',
    'view',
    'seriesType',
    'stacked',
    'seriesTypeMap',
    'selectedDates',
    'styleOverrides',
    'themeID',
    'kpiCompareType',
    'kpiCompareTarget',
    'metricsOrder',
    'nonPivotColumnsOrder',
    'pivotColumnsOrder',
    'tableState',
]

/**
 * Splits a legacy single config object into data, query, and view configs.
 *
 * @param {Object} config - Legacy single config object
 * @returns {Object} - Object with dataConfig, queryConfig, and viewConfig
 */
export const splitConfig = (config) => {
    if (!config) {
        return {
            dataConfig: null,
            queryConfig: null,
            viewConfig: null,
        }
    }

    const dataDefaults = getDefaultDataConfig()
    const queryDefaults = getDefaultQueryConfig()
    const viewDefaults = getDefaultViewConfig()

    const dataConfig = {}
    const queryConfig = {}
    const viewConfig = {}

    // Assign each property to the correct config based on keys
    DATA_CONFIG_KEYS.forEach((key) => {
        dataConfig[key] = config[key] !== undefined ? config[key] : dataDefaults[key]
    })

    QUERY_CONFIG_KEYS.forEach((key) => {
        queryConfig[key] = config[key] !== undefined ? config[key] : queryDefaults[key]
    })

    VIEW_CONFIG_KEYS.forEach((key) => {
        viewConfig[key] = config[key] !== undefined ? config[key] : viewDefaults[key]
    })

    return { dataConfig, queryConfig, viewConfig }
}

/**
 * Merges data, query, and view configs back into a single object.
 * Used for saving and backwards compatibility.
 *
 * @param {Object} dataConfig - Data configuration
 * @param {Object} queryConfig - Query configuration
 * @param {Object} viewConfig - View configuration
 * @returns {Object} - Merged single config object
 */
export const mergeConfigs = (dataConfig, queryConfig, viewConfig) => {
    return {
        ...dataConfig,
        ...queryConfig,
        ...viewConfig,
    }
}

/**
 * Checks if a config key affects data fetching.
 * Useful for determining if a change should trigger an API refetch.
 *
 * @param {string} key - Config property key
 * @returns {boolean} - True if the key affects data fetching
 */
export const isDataAffectingKey = (key) => {
    return DATA_CONFIG_KEYS.includes(key) || QUERY_CONFIG_KEYS.includes(key)
}

/**
 * Checks if a config key is view-only.
 * View-only changes don't require API refetch.
 *
 * @param {string} key - Config property key
 * @returns {boolean} - True if the key is view-only
 */
export const isViewOnlyKey = (key) => {
    return VIEW_CONFIG_KEYS.includes(key)
}

// Export key lists for reference
export { DATA_CONFIG_KEYS, QUERY_CONFIG_KEYS, VIEW_CONFIG_KEYS }
